<template>
  <div class="my-4 mx-2 flex justify-center text-center">
    <div>
      <div class="text-lg font-bold">
        <h1>
          Calculate your Price
        </h1>
      </div>

      <Divider></Divider>

      <div v-show="user && user.isAdmin" class="flex-col mb-4">
        <h2>
          Single Garment Charge
        </h2>
        <div>
          <MoneyInput ref="garmentCharge" initValue="0"></MoneyInput>
        </div>
      </div>

      <div class="flex-col">
        <h2>
          How many will you need?
        </h2>
        <div>
          <NumberInput ref="totalNeed"></NumberInput>
        </div>
      </div>

      <Divider></Divider>

      <div class="flex-col">
        <h2 class="mb-4">
          How many ink colors are in your design?
        </h2>
        <div class="md:flex md:justify-around">
          <NumberSelector class="sm:mb-8" ref="frontSideColors" label="Front Side"></NumberSelector>
          <NumberSelector class="sm:mb-8" ref="backSideColors" label="Back Side"></NumberSelector>
          <NumberSelector class="sm:mb-8" ref="rSleeveColors" label="R. Sleeve"></NumberSelector>
          <NumberSelector class="sm:mb-8" ref="lSleeveColors" label="L. Sleeve"></NumberSelector>
        </div>
      </div>

      <Divider></Divider>

      <div class="flex-col">
        <h2 class="mb-8">
          What color is your garment?
        </h2>
        <div>
          <RadioGroup ref="garmentColor" :options="garmentColorOptions"></RadioGroup>
        </div>
      </div>

      <Divider></Divider>

      <div class="flex-col">
        <h2 class="mb-4">
          Additional Charges.
        </h2>
        <div class="md:flex md:justify-around mb-8">
          <RadioSelector
            class="sm:mb-8"
            v-for="option in addlOptions"
            ref="addlOptionSelectors"
            :key="option.name"
            :option="option">
          </RadioSelector>
        </div>
      </div>

      <div class="md:w-1/2 px-2 mx-auto flex-col items-center">
        <div @click="calculateQuote">
          <JButton :label="getQuoteBtnText" :isWhite="false"></JButton>
        </div>

        <!-- Quote total display -->
        <div class="flex-col mt-4 w-full">
          <transition @before-enter="beforeEnter" @enter="enter" @leave="leave" :css="false">
            <div v-if="quoteTotal > 0">
              <div class="flex flex-col">
                <div class="flex items-end justify-center">
                  <template v-if="showingTotal">
                    <span style="font-size: 5.5em">${{ quoteTotalDollars }}</span>
                    <span style="font-size: 2em" class="mb-4">.{{ quoteTotalCents }}</span>
                  </template>
                  <template v-else>
                    <span style="font-size: 5.5em">${{ quoteEachDollars }}</span>
                    <span style="font-size: 2em" class="mb-4">.{{ quoteEachCents }}</span>
                  </template>
                </div>
                <div class="my-4 text-xl">
                  <span v-if="showingEach">each</span>
                  <span v-else>total</span>
                </div>
              </div>
            </div>
          </transition>
          <div class="flex">
            <div class="mr-2 w-full">
              <JButton @click="showEach" label="each" :isWhite="!showingEach"></JButton>
            </div>
            <div class="mr-2 w-full">
              <JButton @click="showTotal" label="total" :isWhite="!showingTotal"></JButton>
            </div>
            <div class="w-full">
              <JButton @click="reset" label="Reset" :isWhite="false"></JButton>
            </div>
          </div>
        </div>
      </div>

      <div class="text-left mt-6">
        <p><strong>Pricing Considerations:</strong></p>
        <ul class="mt-4">
          <li>
            Multi colored designs on dark garments require an underbase.<br> You must include
            an additional color to receive an accurate price.
          </li>
        </ul>
        <div class="text-center mt-8">
          <span v-if="user && user.isAdmin">
            <router-link to="admin">admin</router-link> |
          </span>
          <a href="" @click="signOut">sign out</a>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
// @ is an alias to /src
import firebase from 'firebase/app'
import 'firebase/firestore'
import 'firebase/auth'
import Velocity from 'velocity-animate'

import NumberSelector from '@/components/NumberSelector'
import NumberInput from '@/components/NumberInput'
import RadioSelector from '@/components/RadioSelector'
import JButton from '@/components/JButton'
import Divider from '@/components/Divider'
import RadioGroup from '@/components/RadioGroup'
import MoneyInput from '@/components/MoneyInput'

export default {
  name: 'home',
  components: {
    NumberSelector,
    NumberInput,
    RadioSelector,
    JButton,
    Divider,
    RadioGroup,
    MoneyInput
  },
  beforeRouteEnter (to, from, next) {
    if (!firebase.auth().currentUser) {
      next('/login')
    } else {
      next()
    }
  },
  data () {
    return {
      quoteTotal: 0,
      quoteTotalDollars: 127,
      quoteTotalCents: 83,
      quoteEachDollars: 0,
      quoteEachCents: 0,
      quote: {},
      showingEach: false,
      showingTotal: true,
      user: null,
      garmentColorOptions: [
        {
          name: 'darkGarments',
          label: 'dark garment'
        },
        {
          name: 'lightGarments',
          label: 'light garment'
        }
      ],
      pricing: null,
      colorPricingGrid: [],
      addlOptions: [],
      getQuoteBtnText: 'Get Quote'
    }
  },
  async mounted () {
    // gather addlOptions
    const addlOptionsSnapshot = await firebase.firestore().collection(`addlOptions`).get()
    addlOptionsSnapshot.forEach(option => {
      this.addlOptions.push(option.data())
    })

    this.user = await this.getLoggedInUser()
  },
  methods: {
    /* TRANSITION FUNCTIONS */
    beforeEnter (el) {
      el.style.opacity = 0
      el.style.height = '0px'
    },
    enter (el, done) {
      Velocity(el, { opacity: 1, height: '100%' }, { duration: 500 })
    },
    leave (el, done) {
      Velocity(el, { opacity: 0, height: 0 }, { duration: 600 })
    },

    async getLoggedInUser () {
      if (!this.user) {
        const loggedInUser = await firebase.auth().currentUser
        if (loggedInUser) {
          const uid = loggedInUser.uid
          const userData = await firebase.firestore().doc(`users/${uid}`).get()
          this.user = userData.data()
        }
      }

      return this.user
    },
    showEach () {
      this.showingEach = true
      this.showingTotal = false
    },
    showTotal () {
      this.showingEach = false
      this.showingTotal = true
    },
    async calculateQuote () {
      this.getQuoteBtnText = 'Calculating...'
      this.user = await this.getLoggedInUser()

      this.quoteTotal = 0
      await this.getPricing(this.user.tier) // TODO: get from logged in user
      this.quote = {
        totalNeed: this.$refs.totalNeed.getValue(),
        garmentColor: this.$refs.garmentColor.getSelectedOption(),
        frontSideColors: this.$refs.frontSideColors.getValue(),
        backSideColors: this.$refs.backSideColors.getValue(),
        lSleeveColors: this.$refs.lSleeveColors.getValue(),
        rSleeveColors: this.$refs.rSleeveColors.getValue(),
        garmentCharge: this.$refs.garmentCharge.getValue(),
        addlOptions:
          this.$refs.addlOptionSelectors
            .filter(selector => selector.isSelected())
            .map(selector => selector.getValue())
      }

      const totalColors = this.quote.frontSideColors + this.quote.backSideColors + this.quote.lSleeveColors + this.quote.rSleeveColors

      // setup fee
      const totalSetupFee = this.pricing.setup * totalColors
      this.quoteTotal += totalSetupFee

      // colors per side
      const frontSideTotal = await this.getPriceForNumColorsAndQty(this.quote.frontSideColors, this.quote.totalNeed)
      const backSideTotal = await this.getPriceForNumColorsAndQty(this.quote.backSideColors, this.quote.totalNeed)
      const lSleeveTotal = await this.getPriceForNumColorsAndQty(this.quote.lSleeveColors, this.quote.totalNeed)
      const rSleeveTotal = await this.getPriceForNumColorsAndQty(this.quote.rSleeveColors, this.quote.totalNeed)
      console.log(frontSideTotal, backSideTotal, lSleeveTotal, rSleeveTotal)
      const perShirtCost = frontSideTotal + backSideTotal + lSleeveTotal + rSleeveTotal
      this.quoteTotal += perShirtCost * this.quote.totalNeed

      // dark or light garment
      let numLocations = 0
      if (frontSideTotal > 0) numLocations++
      if (backSideTotal > 0) numLocations++
      if (lSleeveTotal > 0) numLocations++
      if (rSleeveTotal > 0) numLocations++
      this.quoteTotal += this.pricing[this.quote.garmentColor.name] * numLocations * this.quote.totalNeed

      // additional options
      for (let selectedOption of this.quote.addlOptions) {
        const cost = this.pricing.addlOptions[selectedOption.name]
        this.quoteTotal += cost * this.quote.totalNeed
      }

      if (this.quote.garmentCharge) {
        const garmentCharge = this.quote.garmentCharge * this.quote.totalNeed
        this.quoteTotal += garmentCharge
      }

      // break total quote into dollars and cents
      const totalQuoteParts = (this.quoteTotal / 100).toFixed(2).toString().split('.')
      this.quoteTotalDollars = totalQuoteParts[0]
      this.quoteTotalCents = totalQuoteParts[1]

      const eachParts = (this.quoteTotal / this.quote.totalNeed / 100).toFixed(2).toString().split('.')
      this.quoteEachDollars = eachParts[0]
      this.quoteEachCents = eachParts[1]
    },
    async getPricing (tier) {
      if (!this.pricing) {
        // build pricing object for tier of logged in user
        this.pricing = {
          addlOptions: {}
        }

        const items = ['darkGarments', 'lightGarments', 'setup']
        for (let item of items) {
          this.pricing[item] = await this.getPricingFor(item, tier)
        }

        for (let option of this.addlOptions) {
          this.pricing.addlOptions[option.name] = await this.getPricingFor(option.name, tier)
        }

        await this.initColorPricingGrid(tier)
      }

      this.getQuoteBtnText = 'Get Quote'

      return this.pricing
    },
    async initColorPricingGrid (tier) {
      if (this.colorPricingGrid.length === 0) {
        const priceGridCollection = firebase.firestore().collection(`/pricing/colors/${tier}`)
        const priceGrid = await priceGridCollection.get()
        priceGrid.forEach(pricing => {
          this.colorPricingGrid.push(pricing.data())
        })
      }
      return this.colorPricingGrid
    },
    async getPriceForNumColorsAndQty (numColors, quantity) {
      if (numColors > 0) {
        await this.initColorPricingGrid()

        const minQty = Math.min(...this.colorPricingGrid.filter(price => price.maxQty >= quantity).map(price => price.maxQty))
        const prices = this.colorPricingGrid.find(price => {
          return price.maxQty === minQty
        })

        return prices.sidePrices[numColors]
      }

      return 0
    },
    async getPricingFor (item, tier) {
      return (await firebase.firestore().doc(`/pricing/${item}`).get()).data()[tier]
    },
    async signOut () {
      await firebase.auth().signOut()
    },
    reset () {
      const confirmed = window.confirm('This will delete all your changes. Are you sure?')
      if (confirmed) {
        this.quoteTotal = 0
        this.quoteTotalDollars = 0
        this.quoteTotalCents = 0
        this.quoteEachDollars = 0
        this.quoteEachCents = 0
        Object.keys(this.$refs).forEach(refKey => {
          const ref = this.$refs[refKey]
          if (Array.isArray(ref)) {
            ref.forEach(ref => {
              if (ref.hasOwnProperty('reset')) {
                ref.reset()
              }
            })
          }
          if (ref.hasOwnProperty('reset')) {
            ref.reset()
          }
        })
      }
    }
  }
}
</script>
